import { useState, useCallback, useRef, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import documentService from '../api/documentService';
import useDocumentStore from '../stores/documentStore';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from './useAuth';
import { 
  DocumentMetadata, 
  UploadQueueItem,
  DocumentType
} from '../types/document';

interface UseDocumentUploadOptions {
  onUploadComplete?: (document: any) => void | Promise<void>;
  onUploadError?: (error: Error, file: File) => void;
  suppressErrorToast?: boolean;
}

interface UseDocumentUploadReturn {
  uploadQueue: UploadQueueItem[];
  startUpload: (files: File[], documentType?: DocumentType) => void;
  cancelUpload: (uploadId: string) => void;
  retryUpload: (uploadId: string) => void;
  removeFromQueue: (uploadId: string) => void;
  isUploading: boolean;
  error: string | null;
}

const useDocumentUpload = (options: UseDocumentUploadOptions = {}): UseDocumentUploadReturn => {
  const auth = useAuth() as any;
  const isAuthenticated = () => {
    try {
      if (typeof auth?.isAuthenticated === 'function') return !!auth.isAuthenticated();
      return !!auth?.isAuthenticated;
    } catch {
      return false;
    }
  };
  const getAuthHeaders = () => {
    try {
      if (typeof auth?.getAuthHeaders === 'function') return auth.getAuthHeaders();
      return {} as Record<string, string>;
    } catch {
      return {} as Record<string, string>;
    }
  };
  const [uploadQueue, setUploadQueue] = useState<UploadQueueItem[]>([]);
  const [uploadErrors, setUploadErrors] = useState<Record<string, string>>({});
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const xhrRefs = useRef<Record<string, XMLHttpRequest>>({});
  const addDocument = useDocumentStore(state => state.addDocument);
  const fetchUserQuota = useDocumentStore(state => state.fetchUserQuota);
  const { showToast } = useToast();
  
  // WebSocket removed for documents; rely on polling/status
  
  // Subscribe to document store updates to sync processing status
  useEffect(() => {
    const unsubscribe = useDocumentStore.subscribe((state) => {
      const documents = state.documents;
      setUploadQueue(prev => {
        // Only update if there's an actual change to prevent infinite loops
        let hasChanged = false;
        const newQueue = prev.map(item => {
          if (item.documentId) {
            const doc = documents.find(d => String(d.doc_id) === item.documentId);
            if (doc && (
              item.processingStatus !== doc.processing_status ||
              item.processingError !== doc.processing_error
            )) {
              hasChanged = true;
              return {
                ...item,
                processingStatus: doc.processing_status,
                processingError: doc.processing_error
              };
            }
          }
          return item;
        });
        return hasChanged ? newQueue : prev;
      });
    });
    return unsubscribe;
  }, []);

  // Poll for document status updates on processing documents with exponential backoff
  useEffect(() => {
    // Create a stable key for processing documents to avoid re-triggering
    const processingDocuments = uploadQueue.filter(
      item => item.documentId && 
      item.processingStatus && 
      ['pending', 'queued', 'processing'].includes(item.processingStatus)
    );
    
    // Create stable document IDs list to track actual changes
    const docIds = processingDocuments.map(d => d.documentId).sort().join(',');

    if (processingDocuments.length === 0) {
      return;
    }

    // Track polling attempts and intervals for each document
    const pollAttempts = new Map<string, number>();
    const timeouts = new Set<NodeJS.Timeout>();

    const pollDocument = async (item: typeof processingDocuments[0], attemptNumber: number = 0) => {
      try {
        const _auth2 = getAuthHeaders();
        const document = (_auth2 && Object.keys(_auth2).length > 0)
          ? await documentService.getDocument(
              item.documentId!,
              _auth2
            )
          : await documentService.getDocument(
              item.documentId!
            );
        
        // Update the document in the store, which will trigger the subscription above
        const storeAny: any = (useDocumentStore as any);
        const updateDocumentFn = typeof storeAny?.getState === 'function' ? storeAny.getState().updateDocument : undefined;
        if (typeof updateDocumentFn === 'function') {
          updateDocumentFn(document.doc_id, {
            processing_status: document.processing_status,
            processing_error: document.processing_error,
            word_count: document.word_count,
            extracted_text: document.extracted_text,
            ai_insights: document.ai_insights,
            processing_cost: document.processing_cost,
            processing_completed_at: document.processing_completed_at
          });
        }
        
        // If still processing, schedule next poll with exponential backoff
        if (['pending', 'queued', 'processing'].includes(document.processing_status)) {
          // Calculate next poll interval: 3s, 6s, 12s, 24s, then cap at 30s
          const nextInterval = Math.min(3000 * Math.pow(2, attemptNumber), 30000);
          const timeoutId = setTimeout(() => {
            pollDocument(item, attemptNumber + 1);
          }, nextInterval);
          timeouts.add(timeoutId);
        } else {
          // Document finished processing, remove from attempts map
          pollAttempts.delete(item.documentId!);
        }
        
      } catch (error: any) {
        console.error(`Failed to poll document ${item.documentId}:`, error);
        
        // If authentication error, stop polling this document
        if (error.message?.includes('Authentication') || error.message?.includes('401')) {
          console.warn(`Stopping polling for document ${item.documentId} due to auth error`);
          pollAttempts.delete(item.documentId!);
          return;
        }
        
        // Otherwise, retry with exponential backoff
        const nextInterval = Math.min(3000 * Math.pow(2, attemptNumber), 30000);
        const timeoutId = setTimeout(() => {
          pollDocument(item, attemptNumber + 1);
        }, nextInterval);
        timeouts.add(timeoutId);
      }
    };

    // Start polling each document
    processingDocuments.forEach(item => {
      if (!pollAttempts.has(item.documentId!)) {
        pollAttempts.set(item.documentId!, 0);
        pollDocument(item, 0);
      }
    });

    // Cleanup function
    return () => {
      // Clear all timeouts
      timeouts.forEach(timeoutId => clearTimeout(timeoutId));
      timeouts.clear();
      pollAttempts.clear();
    };
  }, [uploadQueue.filter(item => 
    item.documentId && 
    item.processingStatus && 
    ['pending', 'queued', 'processing'].includes(item.processingStatus)
  ).map(d => d.documentId).sort().join(','), getAuthHeaders]); // Only re-run when processing document IDs actually change

  // Update isUploading based on queue
  useEffect(() => {
    const hasActiveUploads = uploadQueue.some(item => item.status === 'uploading');
    setIsUploading(hasActiveUploads);
  }, [uploadQueue]);

  // Update upload progress
  const updateUploadProgress = useCallback((uploadId: string, progress: number, speed?: number, timeRemaining?: number) => {
    setUploadQueue(prev => 
      prev.map(item => 
        item.id === uploadId 
          ? { ...item, progress, speed, timeRemaining }
          : item
      )
    );
  }, []);

  // Update upload status
  const updateUploadStatus = useCallback((uploadId: string, status: UploadQueueItem['status'], error?: string) => {
    setUploadQueue(prev => 
      prev.map(item => 
        item.id === uploadId 
          ? { ...item, status, error }
          : item
      )
    );
    
    if (error) {
      setUploadErrors(prev => ({ ...prev, [uploadId]: error }));
    } else {
      setUploadErrors(prev => {
        const { [uploadId]: _, ...rest } = prev;
        return rest;
      });
    }
  }, []);

  // Clear completed uploads
  const clearCompletedUploads = useCallback(() => {
    setUploadQueue(prev => prev.filter(item => item.status !== 'completed'));
  }, []);

  // Helper function to get MIME type from file extension
  const getMimeTypeFromExtension = useCallback((filename: string): string => {
    const ext = filename.toLowerCase().split('.').pop();
    const mimeMap: Record<string, string> = {
      // Document formats
      'pdf': 'application/pdf',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'txt': 'text/plain',
      'rtf': 'application/rtf',
      // Spreadsheet formats
      'xls': 'application/vnd.ms-excel',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'csv': 'text/csv',
      // Presentation formats
      'ppt': 'application/vnd.ms-powerpoint',
      'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      // Image formats
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'gif': 'image/gif',
      'webp': 'image/webp',
      // Data formats
      'json': 'application/json',
      'xml': 'application/xml',
      'yaml': 'application/x-yaml',
      'yml': 'application/x-yaml',
      // Code files
      'py': 'text/x-python',
      'js': 'text/javascript',
      'ts': 'text/x-typescript',
      'java': 'text/x-java',
      'cpp': 'text/x-cpp',
      'c': 'text/x-c',
      'cs': 'text/x-csharp',
      'rb': 'text/x-ruby',
      'go': 'text/x-go',
      'rs': 'text/x-rust',
      'html': 'text/html',
      'css': 'text/css',
      'jsx': 'text/javascript',
      'tsx': 'text/x-typescript',
      'php': 'text/x-php',
      'swift': 'text/x-swift',
      'kt': 'text/x-kotlin',
      // Text formats
      'md': 'text/markdown',
      'markdown': 'text/markdown',
      'tex': 'application/x-latex',
      'latex': 'application/x-latex'
    };
    
    return mimeMap[ext || ''] || 'application/octet-stream';
  }, []);

  // Upload document
  const uploadDocument = useCallback(async (file: File, metadata?: Partial<DocumentMetadata>) => {
    // Check authentication first
    if (!isAuthenticated()) {
      throw new Error('You must be logged in to upload documents');
    }
    
    const uploadId = uuidv4();
    
    // Validate file
    const validation = documentService.validateFile(file);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    // Get MIME type - use browser detection or fallback to extension-based detection
    const mimeType = file.type || getMimeTypeFromExtension(file.name);
    
    // Debug logging removed to prevent console spam

    // Create upload queue item
    const queueItem: UploadQueueItem = {
      id: uploadId,
      file,
      metadata: {
        filename: file.name,
        fileSize: file.size,
        mimeType: mimeType,
        ...metadata
      } as DocumentMetadata,
      progress: 0,
      status: 'queued',
      retryCount: 0
    };

    setUploadQueue(prev => [...prev, queueItem]);

    try {
      // Update status to uploading
      updateUploadStatus(uploadId, 'uploading');

      // Request upload URL
      const _auth = getAuthHeaders();
      const presignedData = _auth && Object.keys(_auth).length > 0
        ? await documentService.requestUploadUrl(
            queueItem.metadata as DocumentMetadata,
            _auth
          )
        : await documentService.requestUploadUrl(
            queueItem.metadata as DocumentMetadata
          );

      // Debug logging removed to prevent console spam

      // Upload to R2 with progress tracking
      let lastProgressTime = Date.now();
      let lastProgressBytes = 0;
      
      // Debug logging removed to prevent console spam

      const etag = await documentService.uploadToR2(
        presignedData.uploadUrl,
        presignedData.uploadFields,
        file,
        (progress) => {
          const currentTime = Date.now();
          const currentBytes = (progress / 100) * file.size;
          const timeDiff = (currentTime - lastProgressTime) / 1000; // seconds
          const bytesDiff = currentBytes - lastProgressBytes;
          
          // Calculate speed (bytes per second)
          const speed = timeDiff > 0 ? bytesDiff / timeDiff : 0;
          
          // Calculate time remaining
          const remainingBytes = file.size - currentBytes;
          const timeRemaining = speed > 0 ? remainingBytes / speed : 0;
          
          updateUploadProgress(uploadId, progress, speed, timeRemaining);
          
          lastProgressTime = currentTime;
          lastProgressBytes = currentBytes;
        },
        presignedData.method || 'POST',  // Use method from response
        presignedData.headers  // Pass headers from response
      );

      // Debug logging removed to prevent console spam

      // Store XHR reference for cancellation
      if (global.XMLHttpRequest && (global.XMLHttpRequest as any).lastInstance) {
        xhrRefs.current[uploadId] = (global.XMLHttpRequest as any).lastInstance;
      }

      // Debug logging removed to prevent console spam

      // Confirm upload
      const document = (_auth && Object.keys(_auth).length > 0)
        ? await documentService.confirmUpload(
            presignedData.documentId,
            etag,
            _auth
          )
        : await documentService.confirmUpload(
            presignedData.documentId,
            etag
          );
      
      // Debug logging removed to prevent console spam

      // Update status to completed with document ID (as string)
      setUploadQueue(prev => 
        prev.map(item => 
          item.id === uploadId 
            ? { 
                ...item, 
                status: 'completed' as const,
                progress: 100,  // Explicitly set progress to 100
                documentId: String(document.doc_id),  // Convert to string to match type
                processingStatus: document.processing_status || 'pending'
              }
            : item
        )
      );
      
      // Add document to store
      addDocument(document);
      
      // Update user quota
      if (_auth && Object.keys(_auth).length > 0) {
        await fetchUserQuota(_auth);
      } else {
        await fetchUserQuota();
      }

      // Show success toast
      showToast({
        title: 'Document uploaded successfully',
        type: 'success'
      });

      // Call onUploadComplete callback
      if (options.onUploadComplete) {
        await options.onUploadComplete(document);
      }

      // Don't auto-clear - let user track upload progress

    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      updateUploadStatus(uploadId, 'error', errorMessage);
      setError(errorMessage);
      
      // Show error toast unless suppressed (some pages use a non-intrusive tray)
      if (!options.suppressErrorToast) {
        showToast({
          type: 'error',
          title: 'Upload failed',
          message: `${file.name}: ${errorMessage}`,
          duration: 8000
        });
      }
      
      // Call onUploadError callback
      if (options.onUploadError && error instanceof Error) {
        options.onUploadError(error, file);
      }
      
      // Update retry count
      setUploadQueue(prev => 
        prev.map(item => 
          item.id === uploadId 
            ? { ...item, retryCount: (item.retryCount || 0) + 1 }
            : item
        )
      );
    }
  }, [addDocument, fetchUserQuota, updateUploadProgress, updateUploadStatus, showToast, options, getMimeTypeFromExtension, getAuthHeaders]);

  // Cancel upload
  const cancelUpload = useCallback((uploadId: string) => {
    const xhr = xhrRefs.current[uploadId];
    if (xhr) {
      xhr.abort();
      delete xhrRefs.current[uploadId];
    }
    
    setUploadQueue(prev => prev.filter(item => item.id !== uploadId));
    setUploadErrors(prev => {
      const { [uploadId]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  // Retry upload
  const retryUpload = useCallback((uploadId: string) => {
    const queueItem = uploadQueue.find(item => item.id === uploadId);
    if (!queueItem) return;

    // Remove old item
    setUploadQueue(prev => prev.filter(item => item.id !== uploadId));
    
    // Upload again
    uploadDocument(queueItem.file, queueItem.metadata);
  }, [uploadQueue, uploadDocument]);

  // Start upload for multiple files
  const startUpload = useCallback((files: File[], documentType?: DocumentType) => {
    // Check authentication before starting any uploads
    if (!isAuthenticated()) {
      showToast({
        title: 'You must be logged in to upload documents',
        type: 'error'
      });
      return;
    }
    
    files.forEach(file => {
      const metadata: Partial<DocumentMetadata> = {
        filename: file.name,
        fileSize: file.size,
        mimeType: file.type,
        documentType
      };
      uploadDocument(file, metadata);
    });
  }, [uploadDocument, isAuthenticated, showToast]);

  // Remove from queue
  const removeFromQueue = useCallback((uploadId: string) => {
    setUploadQueue(prev => prev.filter(item => item.id !== uploadId));
    setUploadErrors(prev => {
      const { [uploadId]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  return {
    uploadQueue,
    startUpload,
    cancelUpload,
    retryUpload,
    removeFromQueue,
    isUploading,
    error
  };
};

export default useDocumentUpload;
export { useDocumentUpload };
